import BasePlayer from './base';
import Events from '../lib/events';
import { extend, formatDecimal, getScript, log } from '../lib/util';
import Chartbeat from './trackers/chartbeat';
import ParselyTracker from './trackers/parsely';

const markLoadTime = 'YouTubeLoadTime';
const markTTFF = 'YouTubeTTFF';
const apiReadyQueue = [];
const youtubeErrors = {
  2: 'The request contains an invalid parameter value.',
  5: 'The requested content cannot be played in an HTML5 player.',
  100: 'The requested video was not found.',
  101: 'The video owner does not allow it to be played in embedded players.',
  150: 'The video owner does not allow it to be played in embedded players.',
};

class YoutubePlayer extends BasePlayer {
  constructor(sel, setup) {
    super(sel, setup);
    this.embedConfig = '';
    this.videoIndex = 0;
    this.resetState();

    // The values below are the default values for a Youtube video.
    // Anything passed in url params (like autoplay=true) overrides these values
    // Volume extend uses the values in the 2nd param as the canonical source if
    // there is a conflict of values
    this.options = extend({
      controls: 1,
      modestbranding: 1,
      rel: 0,
      showInfo: 0,
      timedEvents: {},
      start: setup.params.start_time || 0,
    }, setup.params);

    // Use autoplay setting resolved by the base player.
    this.options.autoplay = this.autoplayVideo;

    if (this.video.ytpp_enabled) {
      this.embedConfig = JSON.stringify({
        // For now, we consider continuous play enabled to set autonav and related videos to true
        // However, they could be separated if we added another flag for it
        autonavRelatedVideos: this.video.continuous_play_enabled,
        disableRelatedVideos: !this.video.continuous_play_enabled,
        relatedChannels: getChannelIds(this.video.provider_class, this.video.youtube_channel),
        relatedVideos: this.video.continuous_play_enabled ? this.video.youtube_playlist_ids : [],
        adsConfig: {
          nonPersonalizedAd: !this.allowPersonalizedCookies(),
          disableAds: !this.allowPersonalizedCookies(),
        }
      });
      delete this.options.rel;
    }

    if (this.asset.parsely_site_id && this.allowPersonalizedCookies()) {
      // NOTE: parsely is not added to `trackers` the parsely code
      // automatically adds event listening to the youtube player
      this.parsely = new ParselyTracker(this);
    }
  }

  load() {
    this.mark(markLoadTime);

    if (!this.api()) {
      if (!window.onYouTubeIframeAPIReady) {
        window.onYouTubeIframeAPIReady = () => apiReadyQueue.forEach(player => player.build());
        getScript('//www.youtube.com/iframe_api');
      }
      apiReadyQueue.push(this);
    } else {
      this.build();
    }
  }

  /**
  * Accesses the proprietary YouTube API, if available.
  */
  api() {
    return window.YT;
  }

  /**
  * Builds the client player instance.
  */
  build() {
    if (this.player) return;
    const YouTube = this.api();

    this.player = new YouTube.Player(this.el.id, {
      videoId: this.video.youtube_id || this.video.provider_video_id,
      host: this.allowPersonalizedCookies() ? 'https://www.youtube.com' : 'https://www.youtube-nocookie.com',
      playerVars: this.options,
      embedConfig: this.embedConfig,
      adsConfig: this.adsConfig,
      events: {
        // Documentation on Youtube iframe events:
        // https://developers.google.com/youtube/iframe_api_reference#Events
        onReady: (event) => {
          if (this.asset.chartbeat_account_id && this.allowPersonalizedCookies()) {
            this.chartbeat = new Chartbeat(this, event.target);
          }

          this.trackTimeSinceMark(Events.LOAD_TIME, markLoadTime);
          this.resetState();
          this.el.addEventListener('playTimeUpdate', () => {
            this.trackQuartiles();
            this.trackThresholds();
          });

          // Autoplay videos skip UNSTARTED and go straight to PLAYING,
          // so we need to manually call on the state when ready.
          if (this.autoplayVideo) {
            this.onStart();
          }
        },
        onStateChange: (evt) => {
          switch (evt.data) {
          case YouTube.PlayerState.UNSTARTED: return this.onStart();
          case YouTube.PlayerState.PLAYING: return this.onPlaying();
          case YouTube.PlayerState.ENDED: return this.onEnded();
          case YouTube.PlayerState.PAUSED: return this.onPaused();
          }
        },
        onError: (err) => {
          this.track({
            action: Events.ERROR,
            value: err.data,
            index: this.videoIndex,
            message: youtubeErrors[err.data] || 'Unknown error.'
          });
        },
      },
      width: '100%',
      height: '100%'
    });
  }

  /**
  * Sets up the state to initial values for tracking
  */
  resetState() {
    if (this.player) {
      this.youtubeId = this.player.getVideoData().video_id;
    }
    this.currentQuartile = 0;
    this.watchedSeconds = 0;
    this.currentThreshold = 0;
    this.eventQueue = [];
    this.staleVideo = false;
  }

  // UNSTARTED state transition handler
  onStart() {
    if (!this.firstPlayTriggered && this.videoIndex === 0) {
      this.mark(markTTFF);
      this.firstPlayTriggered = true;
      this.track({ action: Events.PLAY });
    }
  }

  // PLAYING state transition handler
  onPlaying() {
    this.pauseOtherPlayers();
    this.timeStart();

    if (this.played && this.youtubeId !== this.player.getVideoData().video_id) {
      this.relatedVideoStart();
    }
    if (!this.played && this.videoIndex === 0) {
      this.trackTimeSinceMark(Events.TTFF, markTTFF);
      this.played = true;
      this.track({
        action: Events.START,
        value: this.duration(),
        index: this.videoIndex
      });
    }
  }

  // ENDED state transition handler
  onEnded() {
    this.timeStop();
    this.watchedSeconds = 0;
    this.track({
      action: Events.COMPLETE,
      value: (Math.floor(this.player.getDuration() * 0.25)),
      index: this.videoIndex
    });
  }

  // PAUSED state transition handler
  onPaused() {
    this.timeStop();
  }

  /**
  * Wrapper for the track method
  * Queues the event if the video is currently stale
  */
  track(event) {
    if (this.staleVideo) {
      this.eventQueue.shift(event);
    } else {
      super.track(event);
    }
  }

  relatedVideoStart() {
    // Increment videoIndex so client.js tracks this video as a discovery video
    this.videoIndex = this.videoIndex + 1;
    this.resetState();
    this.setVideoByYoutubeId(this.youtubeId);
  }

  /**
  * Fetches a video object from Volume to ensure new videos track correctly;
  * replaces `video` object with new youtube metadata.
  */
  setVideoByYoutubeId(youtubeId) {
    this.staleVideo = true;

    var ajax = new XMLHttpRequest();
    ajax.open('GET', '/api/videos/by_platform_id/youtube/' + youtubeId, true);
    ajax.onload = () => {
      if (ajax.status === 200) {
        this.video = JSON.parse(ajax.responseText);
      } else {
        this.video = this.getNonVolumeRelatedVideo();
      }
    };
    ajax.onerror = () => {
      this.video = this.getNonVolumeRelatedVideo();
    };
    ajax.onloadend = () => {
      this.staleVideo = false;
      log('SENDING ' + this.eventQueue.length + ' queued events.');
      this.eventQueue.forEach(evt => this.track(evt));
      this.eventQueue.length = 0;
      this.track({
        action: Events.START,
        value: this.duration(),
        index: this.videoIndex
      });
    };
    ajax.send();
  }

  /**
  * Return a stub video if a video is not found in volume
  */
  getNonVolumeRelatedVideo() {
    // Convention to use 0 as the video ID if no video found in volume
    return  {
      'id': 0,
      'title_short': this.player.getVideoData().title,
      'description_short': this.player.getVideoData().description,
      'youtube_id': this.player.getVideoData().video_id,
    };
  }

  duration() {
    return this.player ? this.player.getDuration() : 0;
  }

  percentComplete() {
    return this.player ? (this.player.getCurrentTime() / this.duration() * 100) : 0;
  }

  quartileValue() {
    const value = this.duration() / 4;
    return value > 1 ? Math.floor(value) : formatDecimal(value);
  }

  trackQuartiles() {
    var percentComplete = this.percentComplete();
    if (percentComplete >= 75 && this.currentQuartile === 2) {
      this.currentQuartile = 3;
      this.track({ action: Events.THIRD_QUARTILE, value: this.quartileValue(), index: this.videoIndex });
    } else if (percentComplete >= 50 && this.currentQuartile === 1) {
      this.currentQuartile = 2;
      this.track({ action: Events.SECOND_QUARTILE, value: this.quartileValue(), index: this.videoIndex });
    } else if (percentComplete >= 25 && this.currentQuartile === 0) {
      this.currentQuartile = 1;
      this.track({ action: Events.FIRST_QUARTILE, value: this.quartileValue(), index: this.videoIndex });
    }
  }

  trackThresholds() {
    if (this.currentThreshold < 3 && this.watchedSeconds >= 3) {
      this.currentThreshold = 3;
      this.track({ action: Events.THREE_SECONDS, value: 3, index: this.videoIndex });
    }
    if (this.currentThreshold < 10 && this.watchedSeconds >= 10) {
      this.currentThreshold = 10;
      this.track({ action: Events.TEN_SECONDS, value: 10, index: this.videoIndex });
    }
    if (this.currentThreshold < 30 && this.watchedSeconds >= 30) {
      this.currentThreshold = 30;
      this.track({ action: Events.THIRTY_SECONDS, value: 30, index: this.videoIndex });
    }
  }

  /**
  * Starts video playback.
  */
  play() {
    this.player && this.player.playVideo();
  }

  /**
  * Pauses video playback.
  */
  pause() {
    this.player && this.player.stopVideo();
  }

  /**
  * Destroys the player, releasing for garbage collection.
  */
  destroy() {
    this.player && this.player.destroy();
  }

  /**
   * Unmutes the player.
   */
  unmute() {
    this.player && this.player.unMute();
  }
}

/**
* Takes a brand and channelId, returns an array of channel ids (current + additional) for a brand
* TODO: Move this into the data model and expose for users somehow
*/
function getChannelIds(brand, channelId) {
  // other channels to add for a particular brand
  var otherChannels = {
    'verge': ['txJFU9DgUhfr2J2bveCHkQ'],
    'vrg-sci': ['ddiUEpeqJcYeBxX1IVBKvQ'],
    'polygon': ['Dqau170byfm1ksTQ0jYNpg'],
    'polygon-guides': ['uVxaQDraOja6xKidcmoufA']
  };

  if (channelId.substring(0, 2) === 'UC') {
    channelId = channelId.substring(2);
  }

  var channelIds = [channelId];

  if (brand in otherChannels) {
    channelIds = channelIds.concat(otherChannels[brand]);
  }

  return channelIds;
}

export default YoutubePlayer;
